import { Component, input, model, inject } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DeleteOrderPopupComponent } from '../../popups/delete-order-popup/delete-order-popup.component';
import { LoadingPopupComponent } from '../../popups/loading-popup/loading-popup.component';
import { Order } from '../../shared/classes/order';
import { OrderStatus } from '../../shared/classes/order-status.enum';
import { DocumentType } from '../../shared/classes/document-type.enum';
import { FormsModule } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { DocumentUploaderComponent } from './document-uploader/document-uploader.component';
import { SupplierApiService } from '../../shared/services/supplier-api.service';

@Component({
    selector: 'app-existing-order',
    templateUrl: './existing-order.component.html',
    styleUrls: ['./existing-order.component.css'],
    imports: [FormsModule, MatCheckbox, MatRadioGroup, MatRadioButton, DocumentUploaderComponent]
})
/**
 * The existing order page which displays the fields of the selected order and
 * allows the user to manipulate them.
 */
export class ExistingOrderComponent {
    private dialog = inject(MatDialog);
    private supplierApiService = inject(SupplierApiService);

    /** orders: the user's orders array */
    public readonly orders = input.required<Order[]>();
    /** selectedOrder: the order to be displayed on this component. */
    public readonly selectedOrder = model<Order | null>(null);

    /** documentTypes: all document types (is used by HTML template) */
    public documentTypes: string[] = Object.values(DocumentType);

    private requireSelectedOrder(): Order {
        const selectedOrder = this.selectedOrder();
        if (!selectedOrder) {
            throw new Error('Selected order is required.');
        }
        return selectedOrder;
    }

    /**
     * Uses the supplier API service to save an order and opens a popup while waiting.
     */
    public saveOrder(): void {
        const selectedOrder = this.requireSelectedOrder();
        const promise = (async (): Promise<void> => {
            await this.supplierApiService.updateOrders(selectedOrder);
            selectedOrder.unsaved = false;
            selectedOrder.newOrder = false;
        })();
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;
        dialogConfig.data = {
            promise,
            successMessage: 'Order saved.'
        };
        this.dialog.open(LoadingPopupComponent, dialogConfig);
    }

    /**
     * Uses the supplier API service to submit an order and opens a popup while waiting.
     */
    public async submitOrder(): Promise<void> {
        const selectedOrder = this.requireSelectedOrder();
        const promise = (async (): Promise<void> => {
            selectedOrder.submissionDate = new Date();
            selectedOrder.status = OrderStatus.SUBMITTED;
            try {
                selectedOrder.validate();
                selectedOrder.FlandersID = selectedOrder.selectFlanders
                    ? await this.supplierApiService.getNewID('Flanders')
                    : null;
                selectedOrder.SwordsID = selectedOrder.selectSwords
                    ? await this.supplierApiService.getNewID('Swords')
                    : null;
                selectedOrder.LlanberisID = selectedOrder.selectLlanberis
                    ? await this.supplierApiService.getNewID('Llanberis')
                    : null;
                selectedOrder.LasAngelesID = selectedOrder.selectLasAngeles
                    ? await this.supplierApiService.getNewID('LasAngeles')
                    : null;
                await this.supplierApiService.updateOrders(selectedOrder);
                selectedOrder.unsaved = false;
                selectedOrder.newOrder = false;
            } catch (error) {
                selectedOrder.submissionDate = null;
                selectedOrder.status = OrderStatus.INPROGRESS;
                selectedOrder.FlandersID = null;
                selectedOrder.SwordsID = null;
                throw error;
            }
        })();
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;
        dialogConfig.data = {
            promise,
            successMessage: 'Order submitted.'
        };
        const dialogRef = this.dialog.open(LoadingPopupComponent, dialogConfig);
        dialogRef.afterClosed().subscribe(() => {
            // this.dialog.open(SubmissionSuccessComponent, dialogConfig);
        });
    }

    /**
     * Opens a popup to ensure that the user meant to delete the selected order.
     */
    public promptDeleteOrder(): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;
        const dialogRef = this.dialog.open(DeleteOrderPopupComponent, dialogConfig);
        dialogRef.afterClosed().subscribe((confirm) => {
            if (confirm) {
                this.deleteOrder();
            }
        });
    }

    /**
     * Uses the supplier API service to delete an order and opens a popup while waiting.
     */
    public deleteOrder(): void {
        const selectedOrder = this.requireSelectedOrder();
        const formerStatus = selectedOrder.status;
        selectedOrder.status = OrderStatus.DELETED;
        if (!selectedOrder.newOrder) {
            const promise = (async (): Promise<void> => {
                try {
                    await this.supplierApiService.updateOrders(selectedOrder);
                    this.selectedOrder.set(null);
                } catch (error) {
                    selectedOrder.status = formerStatus;
                    throw error;
                }
            })();
            const dialogConfig = new MatDialogConfig();
            dialogConfig.disableClose = true;
            dialogConfig.autoFocus = true;
            dialogConfig.data = {
                promise,
                successMessage: 'Order deleted.'
            };
            this.dialog.open(LoadingPopupComponent, dialogConfig);
        } else {
            this.selectedOrder.set(null);
        }
    }

    /**
     * Marks the selected order as having unsaved changes.
     */
    public markAsUnsaved(): void {
        this.requireSelectedOrder().unsaved = true;
    }

    /**
     * Decides whether a the selected order as having unsaved changes.
     *
     * @return whether a user should be able to upload the given document type.
     *
     * @throws Error If the given document type is not handled in the method.
     */
    public canUpload(documentType: string): boolean {
        const selectedOrder = this.requireSelectedOrder();
        if (documentType === DocumentType.FAIR) {
            return selectedOrder.needsFirstArticle() && !selectedOrder.justDocuments;
        }
        if (
            documentType === DocumentType.COC ||
            documentType === DocumentType.IDIAMOND ||
            documentType === DocumentType.REACH ||
            documentType === DocumentType.CSP
        ) {
            return !selectedOrder.justDocuments;
        }
        if (documentType === DocumentType.RFQ) {
            return selectedOrder.justDocuments && selectedOrder.requestForQuotation;
        }
        if (documentType === DocumentType.ADDITIONAL) {
            return true;
        }
        throw new Error('DocumentType did not match any listed here.');
    }

    /**
     * Adds a new order based on the selected order to the user's
     * orders array and changes to that view.
     */
    public cloneOrder(): void {
        const newOrder = this.requireSelectedOrder().clone();
        newOrder.index = undefined;
        newOrder.name += ' (Copy)';
        newOrder.status = OrderStatus.INPROGRESS;
        newOrder.startedDate = new Date();
        newOrder.submissionDate = undefined;
        newOrder.unsaved = true;
        newOrder.newOrder = true;
        this.orders().push(newOrder);
        this.selectedOrder.set(newOrder);
    }
}
